import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';

let nextDialogId = 0;

// Material Symbols outlines, all on the same 0 -960 960 960 viewBox.
const ICON_PATHS: { [key: string]: string } = {
	signOut: 'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h280v80H200Zm440-160-55-58 102-102H360v-80h327L585-622l55-58 200 200-200 200Z',
	email: 'M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z'
};

@Component({
	selector: 'app-confirm-dialog',
	templateUrl: './confirm-dialog.component.html',
	styleUrls: ['./confirm-dialog.component.scss']
})
export class ConfirmDialogComponent implements OnDestroy {
	@Input() eyebrow: string;
	@Input() title: string;
	@Input() message: string;
	/** Which glyph sits above the title. Defaults to the sign-out arrow the dialog shipped with. */
	@Input() icon: 'signOut' | 'email' = 'signOut';
	@Input() confirmLabel: string = 'Confirm';
	@Input() cancelLabel: string = 'Cancel';
	// Keeps the dialog open with the confirm button spinning while the caller finishes its request.
	@Input() busy: boolean = false;

	@Output() confirmed = new EventEmitter<void>();
	@Output() cancelled = new EventEmitter<void>();

	@ViewChild('confirmButton') confirmButton: ElementRef<HTMLButtonElement>;
	@ViewChild('dialogCard') dialogCard: ElementRef<HTMLElement>;

	isOpen = false;

	get iconPath(): string {
		return ICON_PATHS[this.icon] || ICON_PATHS.signOut;
	}

	readonly titleId = `confirm-dialog-title-${nextDialogId}`;
	readonly messageId = `confirm-dialog-message-${nextDialogId++}`;

	private previousBodyOverflow: string = null;
	private previouslyFocused: HTMLElement = null;

	open(): void {
		if (this.isOpen) {
			return;
		}

		this.previouslyFocused = document.activeElement as HTMLElement;
		this.previousBodyOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		this.isOpen = true;

		// The card is only in the DOM after the next render pass.
		setTimeout(() => {
			if (this.confirmButton) {
				this.confirmButton.nativeElement.focus();
			}
		});
	}

	close(): void {
		if (!this.isOpen) {
			return;
		}

		this.isOpen = false;
		this.releaseBody();

		if (this.previouslyFocused && document.contains(this.previouslyFocused)) {
			this.previouslyFocused.focus();
		}

		this.previouslyFocused = null;
	}

	confirm(): void {
		if (this.busy) {
			return;
		}

		this.confirmed.emit();
	}

	cancel(): void {
		if (this.busy) {
			return;
		}

		this.close();
		this.cancelled.emit();
	}

	onBackdropClick(event: MouseEvent): void {
		if (event.target === event.currentTarget) {
			this.cancel();
		}
	}

	@HostListener('document:keydown', ['$event'])
	onKeydown(event: KeyboardEvent): void {
		if (!this.isOpen) {
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			this.cancel();
			return;
		}

		if (event.key === 'Tab') {
			this.keepFocusInside(event);
		}
	}

	ngOnDestroy(): void {
		this.releaseBody();
	}

	// Focus must not escape to the page behind the dialog, so Tab wraps around the card's own controls.
	private keepFocusInside(event: KeyboardEvent): void {
		if (!this.dialogCard) {
			return;
		}

		const focusable = Array.from(
			this.dialogCard.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')
		);

		if (focusable.length === 0) {
			return;
		}

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;

		if (event.shiftKey && (active === first || !this.dialogCard.nativeElement.contains(active))) {
			event.preventDefault();
			last.focus();
			return;
		}

		if (!event.shiftKey && (active === last || !this.dialogCard.nativeElement.contains(active))) {
			event.preventDefault();
			first.focus();
		}
	}

	private releaseBody(): void {
		if (this.previousBodyOverflow !== null) {
			document.body.style.overflow = this.previousBodyOverflow;
			this.previousBodyOverflow = null;
		}
	}
}
